import { AuthError, type NextAuthConfig } from "next-auth";
import Credentials from "next-auth/providers/credentials";
import Apple from "next-auth/providers/apple";
import apiFetch from "@/lib/fetch";
import tryParseJSON from "./lib/try-parse-json";
import { InvalidCredentials } from "./@core/errors/InvalidCredentials";

const TOKEN_EXPIRATION = 60 * 60 * 24 * 7; // 1 week

async function refreshAccessToken(token: any) {
  try {
    const response = await apiFetch({
      route: "session/refresh-token",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Refresh-Token": true,
        "Authorization": `Bearer ${token.refreshToken}`,
      },
      body: {},
      handleErrorSilently: false,
    });

    if (!response.accessToken || !response.refreshToken) {
      throw new Error("Invalid refresh token response");
    }

    return {
      ...token,
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      accessTokenExpires: Date.now() + (response.expires || TOKEN_EXPIRATION) * 1000,
    };
  } catch (error) {
    return {
      ...token,
      error: "RefreshAccessTokenError",
    };
  }
}

export const authConfig = {
  secret: process.env.NEXTAUTH_SECRET,
  trustHost: true,
  callbacks: {
    authorized({ auth, request: { nextUrl } }) {
      const isLoggedIn = !!auth?.user;
      const isOnDashboard = nextUrl.pathname.startsWith("/hesabim");
      if (isOnDashboard) {
        return isLoggedIn;
      }
      return true;
    },
    async jwt({ token, account, profile, session, trigger, user }) {
      if (trigger == "update") {
        if (session.user?.likedVenues) {
          token.likedVenues = session.user?.likedVenues;
        }

        if (session.user?.firstname) {
          token.firstname = session.user?.firstname;
        }

        if (session.user?.lastname) {
          token.lastname = session.user?.lastname;
        }

        if (session.user?.email) {
          token.email = session.user?.email;
        }

        if (session?.user?.phone) {
          token.phone = session?.user?.phone;
        }

        if (session?.phone) {
          token.phone = session?.phone;
        }

        return token;
      }

      if (user) {
        const userData = user as any;
        token = {
          ...token,
          ...user,
          accessTokenExpires: Date.now() + userData.accessTokenExpires * 1000
        };
        return token;
      }

      const tokenData = token as any;
      if (Date.now() < tokenData.accessTokenExpires) {
        return token
      }

      if (token?.error == "RefreshAccessTokenError") {
        return null;
      }

      // Access token has expired, try to update it
      return refreshAccessToken(token)
    },
    async session({ session, token, user }) {
      // @ts-ignore
      session = {
        ...session,
        user: {
          // @ts-ignore
          id: token?.id as number,
          email: token.email as string,
          phone: token.phone as string,
          // @ts-ignore
          firstname: token.firstname,
          lastname: token.lastname as string,
          likedVenues: token.likedVenues as number[],
        },
        // @ts-ignore
        token: token.accessToken,
      };
      return session;
    },
  },
  providers: [
    Apple({
      clientId: "",
      clientSecret: "",
    }),
    Credentials({
      // The name to display on the sign in form (e.g. 'Sign in with...')
      name: "credentials",
      // The credentials is used to generate a suitable form on the sign in page.
      // You can specify whatever fields you are expecting to be submitted.
      // e.g. domain, username, password, 2FA token, etc.
      // You can pass any HTML attribute to the <input> tag through the object.
      credentials: {
        email: {
          label: "E-posta Adresiniz",
          type: "text",
          placeholder: "test@gmail.com",
        },
        password: { label: "Şifreniz", type: "password" },
      },
      async authorize(credentials: any, req: any) {
        // Handle social login (Facebook etc.) - user already verified by backend
        if (credentials.socialLogin === "true") {
          return {
            id: credentials.id,
            email: credentials.email,
            firstname: credentials.firstname,
            lastname: credentials.lastname,
            likedVenues: JSON.parse(credentials.likedVenues || "[]"),
            accessToken: credentials.accessToken,
            refreshToken: null,
            accessTokenExpires: Number(credentials.accessTokenExpires) || 864000,
          };
        }

        const res = await apiFetch({
          route: "session",
          method: "POST",
          body: {
            email: credentials.email,
            password: credentials.password,
            remindMe: credentials.remindMe == 'true',
          },
          handleErrorSilently: false,
        }).catch((err) => {
          const parsedJson = tryParseJSON(err?.message);
          return parsedJson;
        });

        // If no error and we have user data, return it
        if (res && res.accessToken) {
          return {
            ...res.userData,
            accessToken: res.accessToken,
            refreshToken: res.refreshToken,
            accessTokenExpires: res.expires,
          };
        }

        const InvalidCredentialsCodes = [404, 401];
        if (InvalidCredentialsCodes.includes(res.statusCode)) {
          throw new InvalidCredentials();
        } else if (res.statusCode !== 201) {
          throw new AuthError(
            "Bir şeyler ters gitti, daha sonra tekrar deneyiniz!",
          );
        }

        // Return null if user data could not be retrieved
        return null;
      },
    }),
  ],
} satisfies NextAuthConfig;
